import { ID_PREFIX_BOX } from "./constants";

const LEGACY_PLUGINS = ["GuideClick", "GuideTypeInput", "GuideTagDisplay"];

function parsePercent(v) {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function legacyToItem(toolbar, box) {
  const pos = (box && box.position) || {};
  const x = parsePercent(pos.x);
  const y = parsePercent(pos.y);
  const width = parsePercent(box && box.width);
  const height = parsePercent(box && box.height);
  const baseItem = {
    id: "mi_" + Math.random().toString(36).slice(2, 10),
    unit: "%",
    x,
    y,
    width,
    height,
    label: "",
  };
  if (toolbar.pluginId === "GuideClick") {
    const s = toolbar.state || {};
    return {
      ...baseItem,
      kind: "click",
      color: "#ff00a0",
      actionType: s.actionType || "click",
      onClickAction: s.onClickAction || "next",
      targetStepId: s.targetStepId || null,
      messageText: s.messageText || null,
    };
  }
  if (toolbar.pluginId === "GuideTypeInput") {
    const s = toolbar.state || {};
    return {
      ...baseItem,
      kind: "input",
      color: "#3b82f6",
      inputType: s.inputType || "text",
      validation:
        s.value != null && s.value !== "" ? { mode: "exact", value: s.value } : { mode: "random" },
      successMessage: s.successMessage || null,
      failureMessage: s.failureMessage || null,
      tag: s.tag || null,
      onClickAction: "next",
      targetStepId: null,
    };
  }
  // GuideTagDisplay
  const s = toolbar.state || {};
  return {
    ...baseItem,
    kind: "tag",
    color: "#a855f7",
    tagRef: s.tagRef || "",
  };
}

/**
 * Pure transform: collapse standalone Guide* boxes into a single
 * GuideMultiInput per scene, preserving any pre-existing GuideMultiInput on
 * the same scene by appending the new items.
 *
 * @param {{ boxesById: object, pluginToolbarsById: object, navItemsById: object, containedViewsById?: object }} snapshot
 * @returns {{ boxesById, pluginToolbarsById, navItemsById, containedViewsById }} a new snapshot
 */
export function migrateLegacyGuides(snapshot) {
  const boxesById = { ...(snapshot.boxesById || {}) };
  const pluginToolbarsById = { ...(snapshot.pluginToolbarsById || {}) };
  const navItemsById = { ...(snapshot.navItemsById || {}) };
  const containedViewsById = { ...(snapshot.containedViewsById || {}) };

  const sceneToLegacy = {};
  const sceneToExistingMi = {};

  Object.keys(pluginToolbarsById).forEach((boxId) => {
    const tb = pluginToolbarsById[boxId];
    if (!tb) return;
    const sceneId = boxesById[boxId] && boxesById[boxId].parent;
    if (!sceneId) return;
    if (LEGACY_PLUGINS.indexOf(tb.pluginId) >= 0) {
      if (!sceneToLegacy[sceneId]) sceneToLegacy[sceneId] = [];
      sceneToLegacy[sceneId].push(boxId);
    } else if (tb.pluginId === "GuideMultiInput") {
      sceneToExistingMi[sceneId] = boxId;
    }
  });

  Object.keys(sceneToLegacy).forEach((sceneId) => {
    const legacyBoxIds = sceneToLegacy[sceneId];
    const newItems = legacyBoxIds.map((boxId) =>
      legacyToItem(pluginToolbarsById[boxId], boxesById[boxId])
    );

    let miBoxId = sceneToExistingMi[sceneId];
    if (miBoxId) {
      const existing = pluginToolbarsById[miBoxId];
      const existingItems =
        existing.state && Array.isArray(existing.state.items) ? existing.state.items : [];
      pluginToolbarsById[miBoxId] = {
        ...existing,
        state: { ...(existing.state || {}), items: existingItems.concat(newItems) },
      };
    } else {
      // Scene IDs are unique store-wide, so embedding sceneId guarantees per-scene
      // uniqueness even when multiple scenes are migrated in the same millisecond.
      miBoxId = ID_PREFIX_BOX + Date.now() + "_mig_" + sceneId;
      boxesById[miBoxId] = {
        id: miBoxId,
        parent: sceneId,
        container: 0,
        page: sceneId,
        position: { x: "0", y: "0", type: "absolute" },
        width: "100%",
        height: "100%",
        level: 0,
        col: 0,
        row: 0,
        draggable: true,
        resizable: true,
        showOverlay: false,
        children: [],
        sortableContainers: {},
        containedViews: [],
        deleting: false,
      };
      // structure.{width,height} are stored as numbers (paired with widthUnit/heightUnit)
      // in toolbarCreator. Keep that contract so editor size controls work after migration.
      pluginToolbarsById[miBoxId] = {
        id: miBoxId,
        pluginId: "GuideMultiInput",
        state: { items: newItems },
        structure: {
          height: 100,
          width: 100,
          widthUnit: "%",
          heightUnit: "%",
          rotation: 0,
          aspectRatio: false,
          horizontalFlip: false,
          verticalFlip: false,
          position: "absolute",
        },
        style: {},
        showTextEditor: false,
      };
      sceneToExistingMi[sceneId] = miBoxId;
    }

    legacyBoxIds.forEach((boxId) => {
      delete boxesById[boxId];
      delete pluginToolbarsById[boxId];
    });

    // Update the parent's `boxes` array regardless of whether the parent is
    // a regular scene (navItemsById) or a contained view (containedViewsById).
    const scene = navItemsById[sceneId];
    if (scene && Array.isArray(scene.boxes)) {
      const filtered = scene.boxes.filter((id) => legacyBoxIds.indexOf(id) < 0);
      const next = filtered.indexOf(miBoxId) < 0 ? [...filtered, miBoxId] : filtered;
      navItemsById[sceneId] = { ...scene, boxes: next };
    }
    const cView = containedViewsById[sceneId];
    if (cView && Array.isArray(cView.boxes)) {
      const filtered = cView.boxes.filter((id) => legacyBoxIds.indexOf(id) < 0);
      const next = filtered.indexOf(miBoxId) < 0 ? [...filtered, miBoxId] : filtered;
      containedViewsById[sceneId] = { ...cView, boxes: next };
    }
  });

  return { boxesById, pluginToolbarsById, navItemsById, containedViewsById };
}
